/**
 * React Query hooks for AMS equipment and assignees (ams-project-asset-tree-view)
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import amsEquipmentService from '../../../../services/amsEquipmentService';

export interface Equipment {
  _id: string;
  id?: string;
  equipmentName?: string;
  name?: string;
  assetType?: string;
  hierarchyLevel?: string;
  parentId?: string;
  parentEquipmentId?: string;
  projectAssetId?: string;
  assetId?: string;
  siteEngineerId?: string;
  siteEngineerName?: string;
  siteSupervisorId?: string;
  siteSupervisorName?: string;
  [key: string]: unknown;
}

export interface EquipmentQueryParams {
  projectId: string;
  amsProjectAuditId?: string | null;
  limit?: number;
  sortBy?: string;
}

export interface EquipmentResponse {
  data: Equipment[];
  total?: number;
  page?: number;
  limit?: number;
}

export function useEquipmentQuery(
  params: EquipmentQueryParams
): UseQueryResult<EquipmentResponse, Error> {
  const { projectId, amsProjectAuditId, limit = 10000, sortBy = 'hierarchyLevel' } = params;

  return useQuery<EquipmentResponse, Error>({
    queryKey: ['ams-equipment', projectId, amsProjectAuditId, { limit, sortBy }],
    queryFn: async () => {
      const response = await amsEquipmentService.getEquipment({
        projectId,
        ...(amsProjectAuditId ? { amsProjectAuditId } : {}),
        limit,
        sortBy,
      });
      return response;
    },
    enabled: !!projectId && !!amsProjectAuditId,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always'
  });
}

export function useAssigneesQuery(projectId: string) {
  return useQuery({
    queryKey: ['assignees', projectId],
    queryFn: async () => {
      const { assignmentService } = await import('../../../../services/assignmentService');
      const response = await assignmentService.getEngineersWithAssignments({ projectId });
      return response?.engineers || [];
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000
  });
}
